/**
 * PlacementList Component
 * Shows text list of parking assignments
 */

import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { bucketToTimeString } from "../bitmap";
import { countTrappedAircraft, findTrappedAircraft } from "../egress";
import { ParkingRequest, Placement, SizeCategory, Spot, Zone } from "../types";

/**
 * Format size category as short label
 * WHY: Show aircraft size at a glance in compact UI
 * EXAMPLES: "very-small" → "VS", "small" → "S", "large" → "L"
 */
const formatSizeLabel = (size: SizeCategory): string => {
  const labels: Record<SizeCategory, string> = {
    "very-small": "VS",
    small: "S",
    midsize: "M",
    "super-midsize": "SM",
    large: "L",
  };
  return labels[size];
};

/**
 * Format size category for tooltip (full name)
 * WHY: Provide clear explanation of abbreviation on hover
 */
const formatSizeTooltip = (size: SizeCategory): string => {
  const tooltips: Record<SizeCategory, string> = {
    "very-small": "Very Small (e.g., Cessna 172)",
    small: "Small (e.g., Citation, Phenom 300)",
    midsize: "Midsize (e.g., Challenger 604)",
    "super-midsize": "Super-Midsize (e.g., Gulfstream G650)",
    large: "Large (e.g., Global 7500, BBJ)",
  };
  return tooltips[size];
};

interface PlacementListProps {
  placement: Placement;
  aircraft: ParkingRequest[];
  spots: Spot[];
  zones: Zone[];
}

export const PlacementList: React.FC<PlacementListProps> = ({
  placement,
  aircraft,
  spots,
  zones,
}) => {
  // Get zone name for a spot
  const getZoneName = (spotId: string): string => {
    const spot = spots.find((s) => s.id === spotId);
    if (!spot) return "?";
    const zone = zones.find((z) => z.id === spot.zoneId);
    return zone?.name || spot.zoneId;
  };

  /**
   * Generate human-readable description of which aircraft are trapped
   *
   * WHY: Users need to understand the consequences of a trapping placement
   * SHOWS: "Blocks N822QS (departs 09:00)" or "Blocks 3 aircraft: N822QS, N697QS, N478QS"
   * USE CASE: Helps operator decide if trap is acceptable (e.g., "worth it for VIP aircraft")
   */
  const getTrapWarningText = (
    request: ParkingRequest,
    spot: Spot,
    trapCount: number
  ): string => {
    // Find specifically which aircraft are trapped (not just count)
    // WHY: Knowing "N822QS is trapped" is more actionable than "1 aircraft trapped"
    const trappedAircraft = findTrappedAircraft(
      request,
      spot,
      placement,
      aircraft,
      spots
    );

    // Single aircraft trap - show detailed info with departure time
    // WHY: Departure time tells operator how long the blockage lasts
    // EXAMPLE: If trapped aircraft departs in 30min, it's a minor issue
    if (trappedAircraft.length === 1) {
      const trapped = trappedAircraft[0];
      return `Blocks ${trapped.tail_number} (departs ${bucketToTimeString(
        trapped.departureBucket
      )})`;
    }

    // Multiple aircraft - show list
    // WHY: Operator needs to see full impact (blocking 5 aircraft is serious)
    return `Blocks ${trapCount} aircraft: ${trappedAircraft
      .map((a) => a.tail_number)
      .join(", ")}`;
  };

  // Sort aircraft by arrival time
  const sortedAircraft = [...aircraft].sort(
    (a, b) => a.arrivalBucket - b.arrivalBucket
  );

  const placedAircraft = sortedAircraft.filter((ac) => placement[ac.id]);
  const unplacedAircraft = sortedAircraft.filter((ac) => !placement[ac.id]);

  return (
    <Paper data-testid="placement-list" elevation={1} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Parking Assignments
      </Typography>

      <Grid container spacing={2}>
        {/* Placed Aircraft Column */}
        <Grid
          item
          xs={12}
          md={placedAircraft.length > 0 && unplacedAircraft.length > 0 ? 7 : 12}
        >
          {placedAircraft.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                Placed ({placedAircraft.length})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        Tail
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        Spot
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        Zone
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        Time
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {placedAircraft.map((ac) => {
                      const p = placement[ac.id];
                      const requestedArrival = bucketToTimeString(
                        ac.arrivalBucket
                      );
                      const requestedDeparture = bucketToTimeString(
                        ac.departureBucket
                      );

                      // Check if this is a segmented placement
                      const isSegmented = "segments" in p;

                      // Calculate actual placement times
                      let actualStart: number;
                      let actualEnd: number;
                      let spotDisplay: string;
                      let zoneDisplay: string;

                      if (isSegmented) {
                        // Segmented placement - show chain with move indicators
                        const segments = p.segments;
                        // Trim to actual aircraft dwell (segments may include buffer)
                        actualStart = Math.max(
                          segments[0].startBucket,
                          ac.arrivalBucket
                        );
                        actualEnd = Math.min(
                          segments[segments.length - 1].endBucket,
                          ac.departureBucket
                        );
                        spotDisplay = segments.map((s) => s.spotId).join(" → ");
                        // Use stored moveCount (actual spot changes) instead of recalculating
                        const moves =
                          "moveCount" in p ? p.moveCount : segments.length - 1;
                        const firstMoveTime =
                          moves > 0
                            ? bucketToTimeString(segments[1].startBucket)
                            : null;
                        zoneDisplay = `${moves} move${moves !== 1 ? "s" : ""}${
                          firstMoveTime ? ` @ ${firstMoveTime}` : ""
                        }`;
                      } else {
                        // Single spot placement - assume full dwell
                        actualStart = ac.arrivalBucket;
                        actualEnd = ac.departureBucket;
                        const { spotId } = p;
                        spotDisplay = spotId;
                        zoneDisplay = getZoneName(spotId);
                      }

                      // Check if placement covers full dwell
                      const coveragePct = Math.round(
                        ((actualEnd - actualStart) /
                          (ac.departureBucket - ac.arrivalBucket)) *
                          100
                      );
                      const isPartial = coveragePct < 100;
                      const actualArrival = bucketToTimeString(actualStart);
                      const actualDeparture = bucketToTimeString(actualEnd);

                      // Calculate how many other aircraft this placement would trap
                      // WHY: Users need to see which placements create egress conflicts
                      // WHEN: Shows as warning icon in the placement list
                      let trapCount = 0;
                      if (!isSegmented) {
                        const spot = spots.find((s) => s.id === spotDisplay);
                        if (spot) {
                          trapCount = countTrappedAircraft(
                            ac,
                            spot,
                            placement,
                            aircraft,
                            spots
                          );
                        }
                      }

                      return (
                        <TableRow
                          key={ac.id}
                          data-testid="placement-item"
                          sx={{
                            "&:hover": {
                              bgcolor:
                                trapCount > 0 ? "warning.100" : "success.50",
                            },
                            bgcolor: isPartial
                              ? "warning.50"
                              : trapCount > 0
                              ? "warning.50"
                              : "transparent",
                          }}
                        >
                          <TableCell
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <strong>{ac.tail_number}</strong>
                              {/* Size badge - shows aircraft size category
                                  WHY: Helps operators quickly identify aircraft size
                                  EXAMPLE: "(S)" for small aircraft, "(L)" for large */}
                              <Tooltip
                                title={formatSizeTooltip(ac.sizeCategory)}
                              >
                                <Chip
                                  label={formatSizeLabel(ac.sizeCategory)}
                                  size="small"
                                  sx={{
                                    height: "16px",
                                    fontSize: "0.65rem",
                                    fontWeight: "bold",
                                    bgcolor: "grey.200",
                                    "& .MuiChip-label": { px: 0.5 },
                                  }}
                                />
                              </Tooltip>
                              {ac.locks && ac.locks.length > 0 && (
                                <Tooltip
                                  title={`${ac.locks.length} spot lock${
                                    ac.locks.length !== 1 ? "s" : ""
                                  }: ${ac.locks
                                    .map(
                                      (l) =>
                                        `${l.spotId}${
                                          l.reason ? ` (${l.reason})` : ""
                                        }`
                                    )
                                    .join(", ")}`}
                                >
                                  <LockIcon
                                    sx={{
                                      fontSize: "0.9rem",
                                      color: "#FF6F00",
                                    }}
                                  />
                                </Tooltip>
                              )}
                              {isPartial && (
                                <Box
                                  component="span"
                                  sx={{
                                    ml: 0.5,
                                    fontSize: "0.7rem",
                                    color: "warning.dark",
                                  }}
                                >
                                  ⚠️
                                </Box>
                              )}
                              {/* Show warning icon if this aircraft blocks others from departing
                                  WHY: Immediate visual feedback that this placement has egress issues
                                  EXAMPLE: N650GL at A01 blocks 5 aircraft behind it */}
                              {trapCount > 0 && !isSegmented && (
                                <Tooltip
                                  title={getTrapWarningText(
                                    ac,
                                    spots.find((s) => s.id === spotDisplay)!,
                                    trapCount
                                  )}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      ml: 0.5,
                                      fontSize: "0.7rem",
                                      color: "warning.dark",
                                    }}
                                  >
                                    ⚠️
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                            }}
                          >
                            {spotDisplay}
                            {/* Show allowed sizes for single-spot placements
                                WHY: Helps operators understand spot restrictions
                                EXAMPLE: "A01 (allows: S, M)" */}
                            {!isSegmented &&
                              (() => {
                                const spot = spots.find(
                                  (s) => s.id === spotDisplay
                                );
                                if (spot && spot.allowedSizes.length < 5) {
                                  const sizeLabels = spot.allowedSizes
                                    .map(formatSizeLabel)
                                    .join(", ");
                                  return (
                                    <Box
                                      component="div"
                                      sx={{
                                        fontSize: "0.65rem",
                                        color: "text.secondary",
                                      }}
                                    >
                                      allows: {sizeLabels}
                                    </Box>
                                  );
                                }
                                return null;
                              })()}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.85rem" }}>
                            {zoneDisplay}
                            {isPartial && (
                              <Box
                                component="div"
                                sx={{
                                  fontSize: "0.7rem",
                                  color: "warning.dark",
                                }}
                              >
                                {coveragePct}% coverage
                              </Box>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isPartial ? (
                              <Box>
                                <Box component="div">
                                  {actualArrival}–{actualDeparture}
                                </Box>
                                <Box
                                  component="div"
                                  sx={{
                                    fontSize: "0.65rem",
                                    color: "text.secondary",
                                  }}
                                >
                                  (req: {requestedArrival}–{requestedDeparture})
                                </Box>
                              </Box>
                            ) : (
                              `${actualArrival}–${actualDeparture}`
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Grid>

        {/* Unplaced Aircraft Column */}
        {unplacedAircraft.length > 0 && (
          <Grid item xs={12} md={placedAircraft.length > 0 ? 5 : 12}>
            <Box>
              <Typography variant="subtitle2" color="error" gutterBottom>
                Unplaced ({unplacedAircraft.length})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        Tail
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        Time
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unplacedAircraft.map((ac) => {
                      const arrivalTime = bucketToTimeString(ac.arrivalBucket);
                      const departureTime = bucketToTimeString(
                        ac.departureBucket
                      );

                      return (
                        <TableRow
                          key={ac.id}
                          sx={{
                            "&:hover": { bgcolor: "error.100" },
                            bgcolor: "error.50",
                          }}
                        >
                          <TableCell
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <strong>{ac.tail_number}</strong>
                              {/* Size badge - shows aircraft size category */}
                              <Tooltip
                                title={formatSizeTooltip(ac.sizeCategory)}
                              >
                                <Chip
                                  label={formatSizeLabel(ac.sizeCategory)}
                                  size="small"
                                  sx={{
                                    height: "16px",
                                    fontSize: "0.65rem",
                                    fontWeight: "bold",
                                    bgcolor: "grey.200",
                                    "& .MuiChip-label": { px: 0.5 },
                                  }}
                                />
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {arrivalTime}–{departureTime}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};
